import { StudentApplicationValues } from '../student/application/StudentApplication';
import { PAGE_SECTIONS } from '../student/application/studentDetails/StudentDetailsFormValues';
import { MultipleVaccineDosesFormNames, SingleVaccineDosesFormNames } from '../student/application/vaccinations/VaccinationsFormValues';
import { SCHOOL_LANGUAGES_PAGE_SECTIONS } from '../student/application/schoolAndLanguages/SchoolAndLanguagesFormValues.ts';
import { HOUSING_PAGE_SECTIONS } from '../student/application/housing/HousingFormValues.ts';
import { STUDENT_FAMILY_SECTIONS } from '../student/application/family/FamilyFormValues.ts';

export type ProcessType = 'application' | 'hostFamily' | 'localCoordinator' | 'dashboard';

// Subkeys of T, used to get the sections' keys
export type SectionKeys<T> = {
  [K in keyof T]: keyof T[K];
}[keyof T];

export type SectionType = {
  label: string;
  key: SectionKeys<StudentApplicationValues>;
};

export type SubRouteType = { url: string; label: string; order: number; sections?: SectionType[] };

export type RouteType = { url: string; subRoutes?: { group?: string; routes: Record<string, SubRouteType> }[] };

export type RoutesType = { [K in ProcessType | 'login' | 'callback']: RouteType };

export const routes: RoutesType = {
  dashboard: { url: '', subRoutes: [{ routes: { home: { url: '', label: 'Home', order: 1 } } }] },
  application: {
    url: 'application',
    subRoutes: [
      {
        routes: {
          interview: {
            url: 'interview',
            label: 'Interview',
            order: 1,
            sections: [
              {
                label: 'Interview Details',
                key: 'interviewDetails',
              },
              {
                label: 'Special Requests',
                key: 'specialRequests',
              },
              {
                label: 'Direct Placement',
                key: 'directPlacement',
              },
              {
                label: 'Student Evaluation',
                key: 'studentEvaluation',
              },
              {
                label: 'English Comprehension Score',
                key: 'englishScore',
              },
            ],
          },
          studentDetails: {
            url: 'details',
            label: 'Student Details',
            order: 2,
            sections: [
              {
                label: 'Personal Information',
                key: PAGE_SECTIONS.personalInformation,
              },
              {
                label: 'Contact Information',
                key: PAGE_SECTIONS.contactInformation,
              },
              {
                label: 'Profile Photo',
                key: PAGE_SECTIONS.yourPhoto,
              },
            ],
          },
          family: {
            url: 'family',
            label: 'Family',
            order: 3,
            sections: [
              {
                label: 'Parents',
                key: STUDENT_FAMILY_SECTIONS.parentsSection,
              },
              {
                label: 'Siblings',
                key: STUDENT_FAMILY_SECTIONS.familyMembers,
              },
              {
                label: 'Friends / Relatives in the USA',
                key: STUDENT_FAMILY_SECTIONS.friendsInUSA,
              },
            ],
          },
          biography: {
            url: 'biography',
            label: 'Biography',
            order: 4,
            sections: [
              {
                label: 'General',
                key: 'general',
              },
              {
                label: 'Favorite Activities',
                key: 'activities',
              },
              {
                label: "Student's Bio",
                key: 'bio',
              },
              {
                label: 'Awards',
                key: 'awards',
              },
            ],
          },
          schoolAndLanguages: {
            url: 'school-and-languages',
            label: 'School & Languages',
            order: 5,
            sections: [
              { label: 'School', key: SCHOOL_LANGUAGES_PAGE_SECTIONS.school },
              { label: 'Languages', key: SCHOOL_LANGUAGES_PAGE_SECTIONS.languages },
            ],
          },
          housing: {
            url: 'housing',
            label: 'Housing, Food, Allergies & Pets',
            order: 6,
            sections: [
              { label: 'Housing', key: HOUSING_PAGE_SECTIONS.housing },
              { label: 'Pets', key: HOUSING_PAGE_SECTIONS.pets },
              { label: 'Placement', key: HOUSING_PAGE_SECTIONS.placement },
              { label: 'Food', key: HOUSING_PAGE_SECTIONS.food },
              { label: 'Allergies', key: HOUSING_PAGE_SECTIONS.allergies },
            ],
          },
          dearFamily: { url: 'dear-family', label: 'Dear Family', order: 7, sections: [{ label: 'Letter', key: 'letter' }] },
          photoAlbum: {
            url: 'photo-album',
            label: 'Photo Album',
            order: 8,
            sections: [
              { label: 'Photo 1', key: 'photo1' },
              { label: 'Photo 2', key: 'photo2' },
              { label: 'Photo 3', key: 'photo3' },
              { label: 'Additional Photos', key: 'additionalPhotos' },
            ],
          },
          documents: {
            url: 'documents',
            label: 'Documents',
            order: 9,
            sections: [
              { label: 'Passport', key: 'passportDocument' },
              { label: 'Birth Certificate', key: 'birthCertificateDocument' },
              { label: "English Teacher's Recommendation", key: 'englishTeacherRecommendationDocument' },
              { label: 'Transcripts', key: 'transcriptsDocument' },
              { label: 'Transcript Grades Form', key: 'transcriptGradesFormDocument' },
              { label: 'Medical Records', key: 'medicalRecordsDocument' },
              { label: 'Record of Physical Examination', key: 'recordPhysicalExaminationDocument' },
              { label: 'Dental Certification', key: 'dentalCertificationDocument' },
              { label: 'Parent Form', key: 'parentFormDocument' },
              { label: 'ELTIS', key: 'eltis' },
            ],
          },
          agreements: {
            url: 'agreements',
            label: 'Agreements',
            order: 10,
            sections: [
              { label: 'Conditions of Participation', key: 'conditionsParticipationDocument' },
              { label: 'Fee Transparency Acknowledgment', key: 'feeTransparencyAcknowledgmentDocument' },
              { label: 'Medical Release', key: 'medicalReleaseDocument' },
              { label: 'Double Placement Permission', key: 'doublePlacementPermissionDocument' },
              { label: 'Single Host Permission', key: 'singleHostPermissionDocument' },
            ],
          },
          vaccinations: {
            url: 'vaccinations',
            label: 'Vaccinations',
            order: 11,
            sections: [
              { label: 'Vaccination Record Form', key: 'vaccinationRecordFormDocument' },
              { label: 'Diphtheria / Tetanus / Pertussis', key: MultipleVaccineDosesFormNames.diphtheria },
              { label: 'Polio', key: MultipleVaccineDosesFormNames.polio },
              { label: 'Hepatitis A', key: MultipleVaccineDosesFormNames.hepatitisA },
              { label: 'Hepatitis B', key: MultipleVaccineDosesFormNames.hepatitisB },
              { label: 'Measles', key: MultipleVaccineDosesFormNames.measles },
              { label: 'Mumps', key: MultipleVaccineDosesFormNames.mumps },
              { label: 'Rubella', key: MultipleVaccineDosesFormNames.rubella },
              { label: 'Meningitis', key: MultipleVaccineDosesFormNames.meningitis },
              { label: 'COVID-19', key: SingleVaccineDosesFormNames.COVID19 },
              { label: 'Tuberculosis Test', key: SingleVaccineDosesFormNames.tuberculosisTest },
            ],
          },
        },
      },
    ],
  },
  hostFamily: {
    url: 'host-family',
    subRoutes: [
      {
        routes: {
          dashboard: { url: '', label: 'Home', order: 1 },
          familyOverview: { url: 'family-overview', label: 'Family Overview', order: 2 },
          familyDetails: { url: 'family-details', label: 'Family Details', order: 3 },
          houseHome: { url: 'house-home', label: 'House & Home', order: 4 },
          community: { url: 'community', label: 'Community', order: 5 },
          photos: { url: 'photos', label: 'Photos', order: 6 },
          personalReferences: { url: 'personal-references', label: 'Personal References', order: 7 },
          agreement: { url: 'agreement', label: 'Host Family Agreement', order: 8 },
        },
      },
    ],
  },
  localCoordinator: {
    url: 'local-coordinator',
    subRoutes: [
      {
        group: 'LOCAL COORDINATOR APPLICATION',
        routes: {
          overview: { url: 'overview', label: 'Overview', order: 1 },
          yourExperience: { url: 'your-experience', label: 'Your Experience', order: 2 },
          personalReferences: { url: 'personal-references', label: 'Personal References', order: 3 },
          agreement: { url: 'agreement', label: 'Local Coordinator Agreement', order: 4 },
        },
      },
      {
        group: 'HOSTING APPLICATION',
        routes: {
          familyOverview: { url: 'family-overview', label: 'Family Overview', order: 5 },
          familyDetails: { url: 'family-details', label: 'Family Details', order: 6 },
          houseHome: { url: 'house-home', label: 'House & Home', order: 7 },
          community: { url: 'community', label: 'Community', order: 8 },
          photos: { url: 'photos', label: 'Photos', order: 9 },
          agreement: { url: 'host-family-agreement', label: 'Host Family Agreement', order: 10 },
        },
      },
    ],
  },
  login: { url: 'login' },
  callback: { url: 'callback' },
};

// Finds key in routes object and returns the route object with its subroutes
export const getSubRoutesFromUrl = (url: string) => {
  const processTypeUrl = url.split('/')[1];
  return Object.entries(routes).find((route) => route[1].url === processTypeUrl)?.[1];
};
